import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, Security } from '@okta/okta-react';
import { Route, BrowserRouter as Router, Routes, useNavigate } from 'react-router-dom';
import HealthCheck from './HealthCheck/healthCheck';
import Routers from './Routers';

let oktaAuth;

try {
  // Determine the correct configuration based on environment
  const getOktaConfig = () => {
    const hostname = window.location.hostname;
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    console.log('Environment detection:', {
      hostname,
      NODE_ENV: process.env.NODE_ENV,
      isDevelopment
    });

    // Production configuration
    if (hostname === 'onboarding-form-app.juniper.net') {
      return {
        issuer: 'https://juniper.okta.com/oauth2/default',
        clientId: '0oa1r1b95ixgkZ2YA0h8',
        redirectUri: 'https://onboarding-form-app.juniper.net/login/callback'
      };
    }
    
    // Development/Local configuration
    return {
      issuer: process.env.REACT_APP_OKTA_ISSUER || 'https://juniper.oktapreview.com/oauth2/default',
      clientId: process.env.REACT_APP_OKTA_CLIENT_ID || '0oav0u04plfIWEAI70h7',
      redirectUri: window.location.origin + '/login/callback'
    };
  };

  const config = getOktaConfig();


  oktaAuth = new OktaAuth({
    ...config,
    scopes: ['openid', 'profile', 'email'],
    pkce: true,
    responseType: ['code'],
    responseMode: 'query',
    devMode: process.env.NODE_ENV === 'development'
  });
  
  // Add event listeners for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    oktaAuth.authStateManager.subscribe((authState) => {
      console.log('Auth state changed:', authState);
    });
    console.log('OktaAuth initialized successfully:', {
      issuer: oktaAuth.options.issuer,
      clientId: oktaAuth.options.clientId,
      redirectUri: oktaAuth.options.redirectUri,
      scopes: oktaAuth.options.scopes
    });
  }
  
  oktaAuth.start();
} catch (error) {
  console.error('Error initializing OktaAuth:', error);
}

const App = () => {
  const navigate = useNavigate();

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    try {
      navigate(toRelativeUrl(originalUri || '/', window.location.origin), { replace: true });
    } catch (error) {
      console.error('Error in restoreOriginalUri:', error);
      navigate('/', { replace: true });
    }
  };

  const onAuthRequired = (oktaAuth) => {
    console.log('Authentication required, redirecting to Okta...');
    console.log('Current URL:', window.location.href);
    console.log('Redirect URI will be:', oktaAuth.options.redirectUri);
    console.log('Full Okta config:', {
      issuer: oktaAuth.options.issuer,
      clientId: oktaAuth.options.clientId,
      redirectUri: oktaAuth.options.redirectUri,
      scopes: oktaAuth.options.scopes,
      responseType: oktaAuth.options.responseType,
      responseMode: oktaAuth.options.responseMode
    });
    
    oktaAuth.signInWithRedirect({
      originalUri: window.location.pathname
    }).catch(error => {
      console.error('Error during sign in redirect:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        errorCode: error.errorCode,
        errorSummary: error.errorSummary
      });
      
      // Log the error details for debugging
      if (error.xhr) {
        console.error('XHR Error details:', {
          status: error.xhr.status,
          statusText: error.xhr.statusText,
          responseText: error.xhr.responseText,
          responseURL: error.xhr.responseURL
        });
      }
    });
  };
  
  if (!oktaAuth) {
    console.error('OktaAuth not initialized');
    return <div>Error: Authentication not initialized</div>;
  }

  return (
    <Security 
      oktaAuth={oktaAuth} 
      restoreOriginalUri={restoreOriginalUri}
      onAuthRequired={onAuthRequired}
    >
      <Routes>
        <Route path="/login/callback" element={<LoginCallback />} />
        <Route path="/healthCheck" element={<HealthCheck />} />
        <Route path="/*" element={<Routers />} />
      </Routes>
    </Security>
  );
};

const RouterApp = () => {
  return (
    <Router future={{ v7_startTransition: true }}>
      <App />
    </Router>
  );
};

export default RouterApp;
